'use client'

import Link from 'next/link'

import RankListItem from './RankListItem'
import RankMoreCta from './RankMoreCta'
import {
  buildModoocarDetailPath,
  RANK_TRACK_CODE,
  useRankImpression,
  useRankTrackingContext,
} from '../_features/rank'

import type { IRankVehicle, TRankTab } from '../_features/rank'

const PREVIEW_ROW_COUNT = 2

interface IRankListProps {
  /** 1~10위 차종 */
  items: IRankVehicle[]
  /** 11위 이후 흐릿한 미리보기 차종 */
  previewItems: IRankVehicle[]
  /** 현재 탭 (지표 결정) */
  tab: TRankTab
  /** 현재 세그먼트 — 상세 진입 시 커스텀 세그먼트 기준 순위로 보도록 넘긴다 */
  segment: string
  /** 세그먼트 전체 차량 수 */
  totalCount: number
  /** 전체보기 버튼 문구 */
  moreButtonText: string
  /** 전체보기 클릭 */
  onMoreClick: () => void
  /** 인앱: 행 우측 chevron 노출 */
  showChevron?: boolean
  /** 흐릿 미리보기 + 전체보기 CTA 노출 (인앱은 전체 노출이라 숨김) */
  showMoreCta?: boolean
}

/**
 * 차종 랭킹 리스트
 */
const RankList = ({
  items,
  previewItems,
  tab,
  segment,
  totalCount,
  moreButtonText,
  onMoreClick,
  showChevron,
  showMoreCta = true,
}: IRankListProps) => {
  const track = useRankTrackingContext()
  const sectionRef = useRankImpression<HTMLDivElement>(() =>
    track.sectionShow(RANK_TRACK_CODE.RANK_LIST, '차종 랭킹 리스트 노출')
  )

  const backdropItems =
    previewItems.length > 0 ? previewItems : items.slice(-PREVIEW_ROW_COUNT)

  return (
    <div ref={sectionRef} className='flex flex-col lg:gap-dimension-x2'>
      {items.map(item => (
        <Link
          key={item.rank}
          href={buildModoocarDetailPath({
            brandName: item.brandName,
            modelName: item.modelName,
            fuelType: item.fuelType,
            segment,
          })}
          className='block'
          aria-label={`${item.brandName} ${item.modelName} 상세 보기`}
          onClick={() =>
            track.itemClick(
              RANK_TRACK_CODE.RANK_LIST,
              'list_item',
              `${item.brandName} ${item.modelName} 클릭`,
              {
                rank: item.rank,
                target: `${item.brandName} / ${item.modelName}`,
              }
            )
          }
        >
          <RankListItem item={item} tab={tab} showChevron={showChevron} />
        </Link>
      ))}

      {showMoreCta && (
        <div className='relative overflow-hidden'>
          {/* 흐릿한 미리보기 행은 배경(absolute)으로 깔고, 높이는 CTA가 결정하게 한다.
              (반대로 하면 CTA가 백드롭 2행 높이를 넘겨 overflow-hidden에 상하단이 잘림) */}
          <div
            aria-hidden
            className='pointer-events-none absolute inset-0 flex flex-col opacity-30 blur-[10px] lg:gap-dimension-x2'
          >
            {backdropItems.map(item => (
              <RankListItem key={item.rank} item={item} tab={tab} />
            ))}
          </div>
          <div className='relative flex items-center justify-center'>
            <RankMoreCta
              totalCount={totalCount}
              buttonText={moreButtonText}
              onMoreClick={onMoreClick}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default RankList
